import { useEffect } from "react";
import { useAppSelector } from "../redux/services/store";
import { useGetSingleUserQuery } from "../redux/services/staffApi";
import { selectUser, setAuthUser } from "../redux/slices/authSlice";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";

export const useSingleUserController = () => {
  const dispatch = useDispatch();
  const userDetails = useAppSelector(selectUser);
  const id = userDetails?.id;

  const { data, isLoading, isFetching } = useGetSingleUserQuery(id ?? "", {
    skip: !id,
    // GET /employees/staffs/:id already reflects a role reassignment as soon
    // as an admin saves it on the backend — poll it (and refetch on tab
    // focus) so an active session picks up the change without a re-login.
    pollingInterval: 30 * 1000,
    refetchOnFocus: true,
  });

  useEffect(() => {
    const fresh = data?.data;
    if (!id || !fresh) return;

    // /employees/staffs/:id returns `role` as a plain string (e.g. "Driver")
    // plus a separate `role_id`, unlike /login which nests { id, name } under
    // `role`. Normalize to the { id, name } shape the rest of the app (e.g.
    // usePermission's getRoleId, SideNav's role.name check) already expects.
    const currentRole = userDetails?.role as { id?: string; name?: string } | string | undefined;
    const freshRole = fresh.role_id
      ? { id: fresh.role_id, name: fresh.role }
      : currentRole;

    const currentRoleId = typeof currentRole === "object" ? currentRole?.id : undefined;
    const freshRoleId = typeof freshRole === "object" ? freshRole?.id : undefined;

    // Compare every field this endpoint can change that the app actually
    // reads off `userDetails` — not just name, so an admin editing a staff
    // member's photo/email/etc doesn't need a re-login to show up either.
    const hasChanged =
      userDetails?.name !== fresh.name ||
      userDetails?.email !== fresh.email ||
      userDetails?.profile_image !== fresh.profile_image ||
      currentRoleId !== freshRoleId;

    if (hasChanged) {
      const token = Cookies.get("token") || "";
      // Merge onto the existing user rather than replacing it wholesale —
      // the staff-detail endpoint doesn't return company/branches/etc, so a
      // raw overwrite would wipe those fields back to their defaults.
      dispatch(
        setAuthUser({
          token,
          user: { ...userDetails, ...fresh, role: freshRole },
        })
      );
    }
  }, [data, id, dispatch]);

  return {
    isLoading,
    isFetching,
    userDetails,
  };
};
